import React, { createContext, useState, useCallback, useContext, useMemo } from 'react'; // Added useMemo
import Modal from './Modal'; // Assuming Modal.jsx is in the same directory or adjust path

// Create the context
export const GlobalModalContext = createContext(null);

/**
 * Provides a global modal state and functions to show/hide it.
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The child components to be wrapped by the provider.
 */
export const GlobalModalProvider = ({ children }) => {
  const [modalContent, setModalContent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalProps, setModalProps] = useState({});

  /**
   * Shows the global modal with specified content and optional props.
   * @param {React.ReactNode} content - The JSX content to display inside the modal.
   * @param {object} [props={}] - Additional props to pass to the Modal component (e.g., className).
   */
  const showModal = useCallback((content, props = {}) => {
    setModalContent(content);
    setModalProps(props);
    setIsModalOpen(true);
  }, []);

  /**
   * Hides the global modal.
   */
  const hideModal = useCallback(() => {
    setIsModalOpen(false);
    setModalContent(null); // Clear content after hiding
    setModalProps({});
  }, []);

  const contextValue = useMemo(() => ({ showModal, hideModal }), [showModal, hideModal]);

  return (
    <GlobalModalContext.Provider value={contextValue}>
      {children}
      <Modal isOpen={isModalOpen} onClose={hideModal} {...modalProps}>
        {modalContent}
      </Modal>
    </GlobalModalContext.Provider>
  );
};

/**
 * Custom hook to easily access the showModal and hideModal functions.
 * @returns {{showModal: function, hideModal: function}}
 */
export const useGlobalModal = () => {
  const context = useContext(GlobalModalContext);
  if (!context) {
    throw new Error('useGlobalModal must be used within a GlobalModalProvider');
  }
  return context;
};
